/**
 * «Мои показатели» — экран этапа S8, §10.9 ТЗ.
 *
 * Официант открывает его, чтобы ответить себе на вопрос заказчика
 * дословно: «почему у меня низкий средний чек». Отсюда устройство: своё
 * число крупно, рядом — с чем его сравнить, и ни одного чужого имени.
 *
 * **Три правила §10.9, которые видно прямо в разметке:**
 *
 * 1. **Метка актуальности обязательна** и приходит с сервера готовой
 *    строкой. Собирать её здесь значило бы однажды забыть собрать.
 * 2. **Данных нет — так и написано.** Прочерк, а не ноль: ноль среднего
 *    чека читается как «чек бесплатный», а не как «смены не было».
 * 3. **Лучший результат — без имени и только при пяти и более
 *    сотрудниках** в должности (D74). Когда людей меньше, строка не
 *    исчезает молча: экран говорит, почему её нет, — иначе сотрудник
 *    решит, что он и есть лучший.
 */

import { useQuery } from '@tanstack/react-query';
import { Empty, ErrorState, Loading } from '@shell/states';
import { studyApi } from '../api';
import type { MentorMetric, MentorMetrics } from '../api';

/** Число с единицей. Прочерк, а не ноль, когда считать не из чего. */
function show(value: string | null, unit: string): string {
  if (value === null) return '—';
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return value;
  if (unit === '₽') return `${Math.round(numeric).toLocaleString('ru-RU')} ₽`;
  if (unit === '%') return `${numeric.toFixed(1)} %`;
  return `${numeric.toFixed(1)} ${unit}`.trim();
}

/**
 * «Было / стало» — §10.9, первая строка таблицы сравнений.
 *
 * У долей разница считается в процентных пунктах, у денег — в процентах;
 * подпись это различает. «Доля выросла на 7 пунктов» и «доля выросла на
 * 7 процентов» — разные утверждения, и второе меньше первого втрое.
 */
function Delta({ metric }: { metric: MentorMetric }) {
  if (metric.delta === null || metric.previous === null) {
    return <span className="faint">сравнить пока не с чем</span>;
  }
  const numeric = Number(metric.delta);
  if (!Number.isFinite(numeric)) return null;

  const suffix = metric.unit === '%' ? ' п.п.' : ' %';
  const tone = Math.abs(numeric) < 0.5 ? 'flat' : numeric > 0 ? 'up' : 'down';
  const sign = numeric > 0 ? '+' : numeric < 0 ? '−' : '';
  const label =
    tone === 'flat'
      ? 'как в прошлую смену'
      : `${sign}${Math.abs(numeric).toFixed(1)}${suffix} к прошлой смене`;

  return <span className={`mentor-delta mentor-delta--${tone}`}>{label}</span>;
}

function MetricRow({ metric, minGroup }: { metric: MentorMetric; minGroup: number }) {
  return (
    <li className="mentor-metric">
      <div className="mentor-metric__head">
        <span className="mentor-metric__title">{metric.title}</span>
        <strong className="mentor-metric__value">{show(metric.value, metric.unit)}</strong>
      </div>

      <div className="mentor-metric__delta">
        <Delta metric={metric} />
      </div>

      <ul className="mentor-metric__peers">
        {metric.team_avg !== null && (
          <li>
            Средний по должности: <b>{show(metric.team_avg, metric.unit)}</b>
          </li>
        )}

        {/* Лучший результат — число без владельца. Имени здесь нет ни в
            каком виде: сотрудник видит планку, а не человека (D67). */}
        {metric.team_best !== null && (
          <li>
            Лучший в должности: <b>{show(metric.team_best, metric.unit)}</b>
          </li>
        )}

        {/* Скрытая строка объясняется, а не пропадает молча: иначе
            сотрудник решит, что лучший — он сам (D74). */}
        {metric.best_hidden && (
          <li className="faint">
            Лучший результат не показывается: в вашей должности меньше {minGroup} человек с
            данными, и «лучший без имени» перестал бы быть без имени.
          </li>
        )}

        {metric.target !== null && (
          <li>
            Норматив: <b>{show(metric.target, metric.unit)}</b>
          </li>
        )}
      </ul>
    </li>
  );
}

export function Mentor() {
  const query = useQuery({
    queryKey: ['study', 'mentor', 'metrics'],
    queryFn: studyApi.mentorMetrics,
  });

  if (query.isLoading) return <Loading />;
  if (query.error) return <ErrorState error={query.error} onRetry={() => query.refetch()} />;

  const data = query.data as MentorMetrics;

  if (!data.available) {
    // Причина приходит с сервера: их три, и они требуют разных действий
    // от разных людей (§10.9, правило 3). Придумывать её здесь значило бы
    // однажды сказать человеку не то.
    return <Empty title="Показателей пока нет" text={data.reason} />;
  }

  return (
    <div className="mentor">
      <p className="mentor__freshness" role="status">
        {data.freshness}
      </p>

      {data.checks !== null && data.checks !== undefined && (
        <p className="faint">Чеков за смену: {data.checks}</p>
      )}

      <ul className="mentor__list">
        {data.metrics.map((metric) => (
          <MetricRow key={metric.code} metric={metric} minGroup={data.min_group_for_best} />
        ))}
      </ul>

      <p className="faint mentor__note">
        Показатели считает финансовый сервис и присылает раз в сутки. Здесь они только
        показываются — своих расчётов у обучения нет.
      </p>
    </div>
  );
}
